import _ from 'underscore';
import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import {
  useId,
  Button,
  Toaster,
  useToastController,
  Toast,
  ToastTitle,
  ToastBody,
  Spinner,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ValidatedFinalUrl as UrlInput } from '@bingads-webui-campaign-react/validated-url-input-fluent-v2';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { CopilotIcon } from '@bingads-webui-react/copilot-ui-base';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { useCopilotRepositoryContext } from '@bingads-webui/inline-copilot';
import { ValidationGroup, withCustomValidation, withError } from '@bingads-webui-react/validation';
import { getCreativeWorkbenchStyles } from './styles';
import { DisplayAdsImageSuggestionCarousel } from './display-ads-suggestion-carousel';
import { useDisplayAdsCopilot } from './use-display-ads-copilot';


const ValidatedFinalUrl = withError(withCustomValidation(UrlInput));

export const CreativeWorkbench = ({
  onFinalURLFocusOut,
  onCreateAds,
  isNewLogo,
  useBrandKit,
  setUseBrandKit,
}) => {
  const [dataModel, setDataModel] = useState({
    data: {},
  });

  const toasterId = useId('toaster');
  const { dispatchToast, dismissToast, updateToast } = useToastController(toasterId);

  const classes = useDefaultStyles(getCreativeWorkbenchStyles);

  const [, setRecommendationContext] = useContext(RecommendationContext);
  const { finalUrl: finalUrlFromContext } = useCopilotRepositoryContext();

  const activity = useContext(ActivityContext);

  const i18n = useContext(I18nContext);

  const FETCH_COUNT = 10;

  const { finalUrlForCopilot, fetch, isBrandKitEmpty, fetchBrandKit } = useDisplayAdsCopilot();

  const onValidate = (result) => {
    setDataModel(result);
  };

  const notifyFinalURLchange = () => {
    const isValid = _.result(dataModel, 'isValid', false);
    const finalUrls = _.result(dataModel, ['data', 'FinalUrls'], undefined);
    const finalUrl = _.first(finalUrls);

    if (isValid) {
      if (activity) {
        activity.signal(`[DisplayAdsCopilot] - Final URL changed to ${finalUrl}`);
      }

      onFinalURLFocusOut(finalUrl);
    }
  };

  const finalUrlValidationProps = {
    data: {
      FinalUrls: dataModel.data.FinalUrls,
    },
    initialValue: {
      FinalUrls: dataModel.data.FinalUrls,
    },
  };

  const addingRecommendationToast = useMemo(() => (
    <Toast>
      <ToastTitle media={<Spinner size="tiny" />}>
        {i18n.getString(_TL_('Adding recommendation'))}
      </ToastTitle>
    </Toast>
  ), [i18n]);

  const errorOnCreateAdsToast = useMemo(() => (
    <Toast>
      <ToastTitle
        action={
          <Button icon={<DismissRegular />} onClick={() => dismissToast(toasterId)} />
        }
      >
        {i18n.getString(_TL_('Error adding recommendation'))}
      </ToastTitle>
      <ToastBody>
        {i18n.getString(_TL_('An error occurred while adding the recommendation. Please try again later.'))}
      </ToastBody>
    </Toast>
  ), [dismissToast, i18n, toasterId]);

  const onSelectImageSuggestion = useCallback((ads) => {
    if (activity) {
      activity.signal(`[DisplayAdsCopilot] - User selected image suggestion - ${JSON.stringify(ads)}`);
    }

    if (ads && ads.length === 1) {
      dispatchToast(addingRecommendationToast, { toastId: toasterId, timeout: -1 });
      onCreateAds(ads)
        .then(() => dismissToast(toasterId))
        .catch((error) => {
          activity.error(`[DisplayAdsCopilot] - Error adding recommendation -- error: ${JSON.stringify(error)} -- ads: ${JSON.stringify(ads)}`);

          updateToast({
            content: errorOnCreateAdsToast,
            intent: 'error',
            toastId: toasterId,
            timeout: 5000,
          });
        });
      return;
    }

    setRecommendationContext({
      openRatioPicker: true,
      creatives: ads,
    });
  }, [activity, addingRecommendationToast, dismissToast, dispatchToast, errorOnCreateAdsToast, onCreateAds, setRecommendationContext, toasterId, updateToast]);

  useEffect(() => {
    if (finalUrlFromContext) {
      setDataModel({
        data: {
          FinalUrls: [finalUrlFromContext],
        },
      });
    }
  }, [finalUrlFromContext]);

  useEffect(() => {
    if (finalUrlForCopilot) {
      fetch(FETCH_COUNT, useBrandKit);
    }
  }, [fetch, finalUrlForCopilot, useBrandKit]);

  useEffect(() => {
    fetchBrandKit();
  }, []);

  return (
    <div className={classNames([classes.creativeWorkbenchRoot, 'creative-workbench-root'])}>
      <div className={classNames([classes.header, 'header'])}>
      <CopilotIcon
        size="small"
        color={isNewLogo ? 'multicolor': 'blue'}
        title={i18n.getString(_TL_('Copilot icon'))}
        iconStyles={classes.copilotIcon}
      />
        {
          i18n.getString(_TL_('AI-generated creatives'))
        }
      </div>
      <div className={classes.description}>
        {
          i18n.getString(_TL_('Our AI pulls text and images from your Final URL to automatically generate creatives for your ads.'))
        }
      </div>
      <div>
        <ValidationGroup validationProps={{
          onValidate,
          data: dataModel,
          stopPropagation: true,
        }}
        >
          <ValidatedFinalUrl
            i18n={i18n}
            validationProps={finalUrlValidationProps}
            additionalProperties={{ onBlur: notifyFinalURLchange }}
          />
        </ValidationGroup>
      </div>
      <div>
        <DisplayAdsImageSuggestionCarousel
          useBrandKit={useBrandKit}
          isBrandKitEmpty={isBrandKitEmpty}
          setUseBrandKit={setUseBrandKit}
          onSelectImageSuggestion={onSelectImageSuggestion}
        />
      </div>
      <Toaster inline toasterId={toasterId} position="bottom" />
    </div>
  );
};

CreativeWorkbench.propTypes = {
  onFinalURLFocusOut: PropTypes.func.isRequired,
  onCreateAds: PropTypes.func.isRequired,
  isNewLogo: PropTypes.bool,
  useBrandKit: PropTypes.bool,
  setUseBrandKit: PropTypes.func,
};

CreativeWorkbench.defaultProps = {
  isNewLogo: false,
};
